<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import CustomStore from 'devextreme/data/custom_store';
import type { LoadOptions } from 'devextreme/data';
import {
  DxDataGrid,
  DxColumn,
  DxScrolling,
} from 'devextreme-vue/data-grid';
import { DxButton } from 'devextreme-vue/button';
import { DxDropDownButton } from 'devextreme-vue/drop-down-button';
import { DxSelectBox } from 'devextreme-vue/select-box';
import { DxTextBox } from 'devextreme-vue/text-box';
import { DxTabPanel, DxItem } from 'devextreme-vue/tab-panel';
import { BottoPagePanel, bottoGridPaging, getAggregatorsLookup, LookupBox, showError, showSuccess } from '@botto/core';
import {
  targetsApi,
  type BreakdownRow,
  type ClickEvent,
  type StatsSummary,
  type TrackerLink,
} from '../../api';
import ClickLogGrid from '../../components/ClickLogGrid.vue';
import DateRangePicker from '../../components/DateRangePicker.vue';
import DimensionFilterFields from '../../components/DimensionFilterFields.vue';
import StatusBadge from '../../components/StatusBadge.vue';
import {
  decodeTargetKey,
  defaultRangeDays,
  formatBotRate,
  formatDateTime,
  formatNumber,
  toApiDate,
} from '../../utils/format';
import { applyDimensionFilters, emptyDimensionFilters } from '../../utils/dimensionFilters';
import { pageFromLoadOptions, trackerGridProps } from '../../utils/gridUi';

const props = defineProps<{
  targetKey: string;
}>();

const router = useRouter();
const tabIndex = ref(0);
const { paging: linksPaging, pager: linksPager } = bottoGridPaging({ pageSize: 50 });
const linksGrid = ref<InstanceType<typeof DxDataGrid> | null>(null);

const targetUrl = computed(() => {
  try {
    return decodeTargetKey(props.targetKey);
  } catch {
    return '';
  }
});

const initRange = defaultRangeDays(7);
const rangeFrom = ref<Date | null>(initRange.from);
const rangeTo = ref<Date | null>(initRange.to);

const summary = ref<StatsSummary | null>(null);
const loadingSummary = ref(false);
/** Total substitute links — updated from store without replacing whole detail objects. */
const linksTotal = ref(0);

const groupOptions = [
  'country',
  'city',
  'device_type',
  'os',
  'browser',
  'language',
  'outcome',
  'day',
  'hour',
  'referer',
  'asn',
];
const groupBy = ref('country');
const breakdown = ref<BreakdownRow[]>([]);
const loadingBreakdown = ref(false);
const breakdownFilters = reactive(emptyDimensionFilters());

const clicks = ref<ClickEvent[]>([]);
const clicksTotal = ref(0);
const page = ref(1);
const pageSize = 50;
const loadingClicks = ref(false);
const exportingCsv = ref(false);
type ExportId = 'csv' | 'csv-full' | 'excel';
const exportItems: { id: ExportId; text: string; icon?: string }[] = [
  { id: 'csv', text: 'CSV' },
  { id: 'csv-full', text: 'CSV full' },
  { id: 'excel', text: 'Excel', icon: 'xlsxfile' },
];
const selectedExportId = ref<ExportId>('csv');
const exportButtonText = computed(() => {
  if (exportingCsv.value) return '…';
  return exportItems.find((i) => i.id === selectedExportId.value)?.text ?? 'CSV';
});
const filters = reactive({
  outcome: '',
  is_bot: '' as '' | '0' | '1',
  country: '',
  language: '',
  device_type: '',
  correlation_id: '',
  aggregator_id: null as number | null,
});

const aggregatorsLookup = getAggregatorsLookup();

const outcomeItems = [
  { value: '', text: 'Все' },
  { value: 'click', text: 'click' },
  { value: 'redirect', text: 'redirect' },
  { value: 'landing', text: 'landing' },
  { value: 'challenge', text: 'challenge' },
  { value: 'expired', text: 'expired' },
  { value: 'paused', text: 'paused' },
];

const botItems = [
  { value: '', text: 'Все' },
  { value: '0', text: 'Human' },
  { value: '1', text: 'Bot' },
];

const summaryCards = computed(() => {
  const s = summary.value;
  if (!s) return [];
  return [
    { label: 'Клики', value: formatNumber(s.clicks) },
    { label: 'Uniques', value: formatNumber(s.unique_clicks) },
    { label: 'Bots', value: formatNumber(s.bots) },
    { label: 'Bot rate', value: formatBotRate(s.bot_rate) },
    { label: 'Redirects', value: formatNumber(s.redirects) },
    { label: 'Landings', value: formatNumber(s.landings) },
    { label: 'Challenges', value: formatNumber(s.challenges) },
    { label: 'Expired', value: formatNumber(s.expired) },
  ];
});

function rangeParams() {
  return {
    target_url: targetUrl.value,
    from: toApiDate(rangeFrom.value),
    to: toApiDate(rangeTo.value),
  };
}

function breakdownParams() {
  return applyDimensionFilters(
    { ...rangeParams(), group_by: groupBy.value },
    breakdownFilters,
  );
}

/**
 * CustomStore must NOT assign new reactive objects (detail/summary) on every load —
 * that re-renders the page, resizes the grid, and retriggers load (infinite loop).
 */
const linksStore = new CustomStore({
  key: 'id',
  loadMode: 'processed',
  async load(loadOptions: LoadOptions) {
    if (!targetUrl.value) return { data: [], totalCount: 0 };
    const { page: p, page_size } = pageFromLoadOptions(loadOptions, linksPaging.pageSize);
    try {
      const res = await targetsApi.getTargetDetail({
        ...rangeParams(),
        page: p,
        page_size,
      });
      const total = res.link_count || 0;
      if (linksTotal.value !== total) linksTotal.value = total;
      return { data: res.links || [], totalCount: total };
    } catch (e) {
      showError(e);
      return { data: [], totalCount: 0 };
    }
  },
});

async function loadSummary() {
  loadingSummary.value = true;
  try {
    summary.value = await targetsApi.targetSummary(rangeParams());
  } finally {
    loadingSummary.value = false;
  }
}

async function loadBreakdown() {
  loadingBreakdown.value = true;
  try {
    const res = await targetsApi.targetBreakdown(breakdownParams());
    breakdown.value = res.items || [];
  } finally {
    loadingBreakdown.value = false;
  }
}

async function loadClicks() {
  loadingClicks.value = true;
  try {
    const params: Parameters<typeof targetsApi.targetClicks>[0] = {
      ...rangeParams(),
      page: page.value,
      page_size: pageSize,
    };
    if (filters.outcome) params.outcome = filters.outcome;
    if (filters.is_bot !== '') params.is_bot = Number(filters.is_bot);
    applyDimensionFilters(params, filters);
    if (filters.correlation_id.trim()) params.correlation_id = filters.correlation_id.trim();
    if (filters.aggregator_id != null) params.aggregator_id = filters.aggregator_id;
    const res = await targetsApi.targetClicks(params);
    clicks.value = res.items || [];
    clicksTotal.value = res.total || 0;
  } finally {
    loadingClicks.value = false;
  }
}

function reloadClicks() {
  page.value = 1;
  loadClicks().catch(showError);
}

async function exportClicksCsv() {
  exportingCsv.value = true;
  try {
    const params: Parameters<typeof targetsApi.targetClicksCsv>[0] = {
      ...rangeParams(),
      variant: 'basic',
    };
    if (filters.outcome) params.outcome = filters.outcome;
    if (filters.is_bot !== '') params.is_bot = Number(filters.is_bot);
    applyDimensionFilters(params, filters);
    if (filters.aggregator_id != null) params.aggregator_id = filters.aggregator_id;
    await targetsApi.targetClicksCsv(params, 'target-clicks.csv');
    showSuccess('CSV скачан');
  } catch (e) {
    showError(e);
  } finally {
    exportingCsv.value = false;
  }
}

async function exportClicksCsvFull() {
  exportingCsv.value = true;
  try {
    const params: Parameters<typeof targetsApi.targetClicksCsv>[0] = {
      ...rangeParams(),
      variant: 'full',
    };
    if (filters.outcome) params.outcome = filters.outcome;
    if (filters.is_bot !== '') params.is_bot = Number(filters.is_bot);
    applyDimensionFilters(params, filters);
    if (filters.aggregator_id != null) params.aggregator_id = filters.aggregator_id;
    await targetsApi.targetClicksCsv(params, 'target-clicks-full.csv');
    showSuccess('CSV (full) скачан');
  } catch (e) {
    showError(e);
  } finally {
    exportingCsv.value = false;
  }
}

async function exportClicksXlsx() {
  exportingCsv.value = true;
  try {
    const params: Parameters<typeof targetsApi.targetClicksXlsx>[0] = {
      ...rangeParams(),
      variant: 'full',
    };
    if (filters.outcome) params.outcome = filters.outcome;
    if (filters.is_bot !== '') params.is_bot = Number(filters.is_bot);
    applyDimensionFilters(params, filters);
    if (filters.aggregator_id != null) params.aggregator_id = filters.aggregator_id;
    await targetsApi.targetClicksXlsx(params, 'target-clicks.xlsx');
    showSuccess('Excel скачан');
  } catch (e) {
    showError(e);
  } finally {
    exportingCsv.value = false;
  }
}

function onExportItemClick(e: { itemData?: { id?: ExportId } }) {
  const id = e.itemData?.id;
  if (id) selectedExportId.value = id;
}

function onExportButtonClick() {
  switch (selectedExportId.value) {
    case 'csv-full':
      void exportClicksCsvFull();
      break;
    case 'excel':
      void exportClicksXlsx();
      break;
    default:
      void exportClicksCsv();
  }
}

function prevClicksPage() {
  if (page.value > 1) {
    page.value -= 1;
    loadClicks().catch(showError);
  }
}

function nextClicksPage() {
  if (page.value * pageSize < clicksTotal.value) {
    page.value += 1;
    loadClicks().catch(showError);
  }
}

function goLink(l: TrackerLink) {
  router.push(`/tracker/links/${l.id}`);
}

async function refreshOverview() {
  await linksGrid.value?.instance?.refresh();
  await loadSummary();
  await nextTick();
  linksGrid.value?.instance?.updateDimensions();
}

async function onRangeChange() {
  try {
    if (tabIndex.value === 0) await refreshOverview();
    else if (tabIndex.value === 1) await loadBreakdown();
    else await reloadClicks();
  } catch (e) {
    showError(e);
  }
}

watch(tabIndex, async (t, prev) => {
  if (t === prev) return;
  try {
    if (t === 0) {
      await nextTick();
      linksGrid.value?.instance?.updateDimensions();
    } else if (t === 1) await loadBreakdown();
    else await loadClicks();
  } catch (e) {
    showError(e);
  }
});

onMounted(async () => {
  void aggregatorsLookup.ensureLoaded();
  if (!targetUrl.value) {
    showError(new Error('Некорректный target key'));
    return;
  }
  try {
    await loadSummary();
    await nextTick();
    linksGrid.value?.instance?.updateDimensions();
  } catch (e) {
    showError(e);
  }
});
</script>

<template>
  <BottoPagePanel title="Target" standalone>
    <template #header>
      <div class="header-meta">
        <a class="back" href="#" @click.prevent="router.push('/tracker/links')">← Ссылки</a>
        <p v-if="targetUrl" class="subtitle mono truncate-title" :title="targetUrl">{{ targetUrl }}</p>
      </div>
    </template>

    <div class="target-page">
      <div class="toolbar">
        <DateRangePicker v-model:from="rangeFrom" v-model:to="rangeTo" @change="onRangeChange" />
      </div>

      <DxTabPanel
        class="target-tabs"
        height="100%"
        v-model:selected-index="tabIndex"
        :defer-rendering="false"
      >
        <DxItem title="Overview">
          <div class="tab-body tab-body--overview">
            <div v-if="summary" class="stat-cards">
              <div v-for="c in summaryCards" :key="c.label" class="stat-card">
                <div class="stat-card__label">{{ c.label }}</div>
                <div class="stat-card__value">{{ c.value }}</div>
              </div>
            </div>
            <div v-else class="empty">{{ loadingSummary ? 'Загрузка…' : 'Нет данных.' }}</div>

            <div class="details-card">
              <div class="details-card__head">
                <h3>Подменные ссылки ({{ formatNumber(linksTotal) }})</h3>
                <DxButton icon="refresh" text="Обновить" styling-mode="outlined" @click="() => linksGrid?.instance?.refresh()" />
              </div>
              <div class="substitute-grid-wrap">
                <DxDataGrid
                  ref="linksGrid"
                  :data-source="linksStore"
                  :remote-operations="{ paging: true, sorting: false, filtering: false }"
                  v-bind="trackerGridProps"
                  :paging="linksPaging"
                  :pager="linksPager"
                  height="100%"
                  width="100%"
                >
                  <DxScrolling mode="standard" />
                  <DxColumn caption="Code" cell-template="codeCell" width="120" />
                  <DxColumn
                    data-field="name"
                    caption="Имя"
                    :calculate-cell-value="(r: any) => r.name || '—'"
                  />
                  <DxColumn data-field="domain_name" caption="Домен" css-class="mono" />

                  <DxColumn data-field="status" caption="Статус" width="110" cell-template="statusCell" />
                  <DxColumn
                    data-field="correlation_id"
                    caption="Correlation ID"
                    css-class="mono"
                    :calculate-cell-value="(r: TrackerLink) => r.correlation_id || '—'"
                  />

                  <template #codeCell="{ data }">
                    <a class="mono link" href="#" @click.prevent="goLink(data.data)">{{ data.data.code }}</a>
                  </template>
                  <template #statusCell="{ data }">
                    <StatusBadge :status="data.data.status" />
                  </template>
                </DxDataGrid>
              </div>
            </div>
          </div>
        </DxItem>

        <DxItem title="Breakdown">
          <div class="tab-body">
            <div class="toolbar">
              <div class="filter-field">
                <label>Group by</label>
                <DxSelectBox
                  v-model:value="groupBy"
                  :items="groupOptions"
                  width="200"
                  @value-changed="() => loadBreakdown().catch(showError)"
                />
              </div>
              <DimensionFilterFields
                v-model:country="breakdownFilters.country"
                v-model:language="breakdownFilters.language"
                v-model:device-type="breakdownFilters.device_type"
                :target-url="targetUrl"
                :from="rangeParams().from"
                :to="rangeParams().to"
                @change="() => loadBreakdown().catch(showError)"
              />
            </div>
            <DxDataGrid
              :data-source="breakdown"
              v-bind="trackerGridProps"
              :loading="loadingBreakdown"
              key-expr="key"
              height="100%"
              width="100%"
            >
              <DxScrolling mode="standard" />
              <DxColumn :caption="groupBy" :calculate-cell-value="(r: BreakdownRow) => r.key || '(empty)'" css-class="mono" />
              <DxColumn data-field="clicks" caption="Клики" width="100" />
              <DxColumn data-field="unique_clicks" caption="Uniques" width="100" />
              <DxColumn data-field="bots" caption="Bots" width="90" />
            </DxDataGrid>
          </div>
        </DxItem>

        <DxItem title="Click log">
          <div class="tab-body">
            <div class="toolbar filters">
              <div class="filter-field">
                <label>Outcome</label>
                <DxSelectBox
                  v-model:value="filters.outcome"
                  :items="outcomeItems"
                  value-expr="value"
                  display-expr="text"
                  width="140"
                  @value-changed="reloadClicks"
                />
              </div>
              <div class="filter-field">
                <label>Bot</label>
                <DxSelectBox
                  v-model:value="filters.is_bot"
                  :items="botItems"
                  value-expr="value"
                  display-expr="text"
                  width="120"
                  @value-changed="reloadClicks"
                />
              </div>
              <DimensionFilterFields
                v-model:country="filters.country"
                v-model:language="filters.language"
                v-model:device-type="filters.device_type"
                :target-url="targetUrl"
                :from="rangeParams().from"
                :to="rangeParams().to"
                @change="reloadClicks"
              />
              <div class="filter-field">
                <label>Correlation ID</label>
                <DxTextBox
                  v-model:value="filters.correlation_id"
                  placeholder="correlation…"
                  width="180"
                  @enter-key="reloadClicks"
                />
              </div>
              <div class="filter-field filter-field--lookup">
                <label>Агрегатор</label>
                <LookupBox
                  v-model="filters.aggregator_id"
                  :lookup="aggregatorsLookup"
                  placeholder="Агрегатор"
                  @update:model-value="reloadClicks"
                />
              </div>
              <DxButton icon="refresh" text="Применить" styling-mode="outlined" @click="reloadClicks" />
              <DxDropDownButton
                :items="exportItems"
                key-expr="id"
                display-expr="text"
                :selected-item-key="selectedExportId"
                :split-button="true"
                :use-select-mode="true"
                styling-mode="outlined"
                icon="download"
                :text="exportButtonText"
                :disabled="exportingCsv || clicksTotal === 0"
                @button-click="onExportButtonClick"
                @item-click="onExportItemClick"
              />
            </div>

            <ClickLogGrid :clicks="clicks" :loading="loadingClicks" show-code />
            <div v-if="clicksTotal > 0" class="pager">
              <DxButton text="Назад" styling-mode="outlined" :disabled="page <= 1" @click="prevClicksPage" />
              <span class="pager__info">Стр. {{ page }} · {{ formatNumber(clicksTotal) }} всего</span>
              <DxButton
                text="Вперёд"
                styling-mode="outlined"
                :disabled="page * pageSize >= clicksTotal"
                @click="nextClicksPage"
              />
            </div>
          </div>
        </DxItem>
      </DxTabPanel>
    </div>
  </BottoPagePanel>
</template>

<style scoped>
.target-page {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  gap: 12px;
}

.header-meta {
  flex: 1;
  min-width: 0;
}

.back {
  display: inline-block;
  margin-bottom: 4px;
  font-size: 0.875rem;
  color: var(--dx-color-text-secondary, #666);
  text-decoration: none;
}

.back:hover {
  text-decoration: underline;
}

.subtitle {
  margin: 0;
  font-size: 0.875rem;
  color: var(--dx-color-text-secondary, #666);
}

.truncate-title {
  max-width: 720px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.toolbar {
  flex: 0 0 auto;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-field label {
  font-size: 12px;
  color: var(--dx-color-text-secondary, #666);
}

.filter-field--lookup {
  min-width: 220px;
}

.target-tabs {
  flex: 1 1 auto;
  min-height: 0;
}

.target-tabs :deep(.dx-tabpanel-container),
.target-tabs :deep(.dx-multiview-wrapper),
.target-tabs :deep(.dx-multiview-item-container),
.target-tabs :deep(.dx-multiview-item),
.target-tabs :deep(.dx-multiview-item-content) {
  height: 100% !important;
  min-height: 0;
}

.tab-body {
  height: 100%;
  min-height: 0;
  padding-top: 8px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  gap: 12px;
}

.tab-body > :deep(.dx-datagrid),
.tab-body > :deep(.click-log-grid) {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
}

.stat-cards {
  flex: 0 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
}

.stat-card {
  padding: 12px 14px;
  border: 1px solid var(--dx-color-border, #e0e0e0);
  border-radius: 6px;
  background: var(--dx-component-color-bg, #fff);
}

.stat-card__label {
  font-size: 12px;
  color: var(--dx-color-text-secondary, #666);
  margin-bottom: 4px;
}

.stat-card__value {
  font-size: 1.25rem;
  font-weight: 600;
}

.details-card {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 14px;
  border: 1px solid var(--dx-color-border, #e0e0e0);
  border-radius: 6px;
  gap: 8px;
}

.details-card__head {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.details-card h3 {
  margin: 0;
  font-size: 1rem;
}

.substitute-grid-wrap {
  flex: 1 1 auto;
  min-height: 200px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.substitute-grid-wrap > :deep(.dx-datagrid) {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
}

.empty {
  color: var(--dx-color-text-secondary, #666);
  font-size: 0.875rem;
}

.pager {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 12px;
}

.pager__info {
  font-size: 0.875rem;
  color: var(--dx-color-text-secondary, #666);
}

.link {
  color: var(--dx-color-primary, #1976d2);
  text-decoration: none;
}

.link:hover {
  text-decoration: underline;
}

.details-grid {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 6px 12px;
  font-size: 0.875rem;
}

.detail-expand {
  padding: 8px 12px;
}

.muted {
  color: var(--dx-color-text-secondary, #666);
}

:deep(.mono) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.9em;
}
</style>
